import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [legalCurrent, setLegalCurrent] = useState(null);
  const [status, setStatus] = useState("checking"); // "checking" | "authed" | "guest"
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setLegalCurrent(data.legalCurrent || null);
      setStatus("authed");
    } catch (e) {
      setUser(null);
      setLegalCurrent(null);
      setStatus("guest");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      setStatus("authed");
      return { ok: true };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const register = useCallback(async (email, password, name, legalVersions) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/register", {
        email, password, name,
        acceptPrivacyPolicyVersion: legalVersions?.privacy || "",
        acceptTermsOfServiceVersion: legalVersions?.terms || "",
      });
      setUser(data.user);
      setStatus("authed");
      return { ok: true, subscriptionStatus: data.user?.subscriptionStatus || "free" };
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    setUser(null);
    setStatus("guest");
    // Also drop any child profile selection
    try {
      localStorage.removeItem("wmleye:childProfileId");
    } catch (_) {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, legalCurrent, status, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
